import {CheckSeverity} from './app-data-checks';

export interface PfTargetUwySummaryRow {
    bucket: string;
    count?: number;
    countPercent?: number;
    gnwpEur?: number;
    nlr?: number;
    indicativeTargetRateChange?: number;
    targetRateChange?: number;
    targetRetentionRate?: number;
    renewalGnwpEur?: number;
    renewalClaimsEur?: number;
    renewalNlr?: number;

    // Expiring portfolio tracking (GNWP/NLR renewed, lost, remaining)
    expGnwpEurRenewed?: number;
    expGnwpEurLostCancelled?: number;
    expGnwpEurRemaining?: number;

    expNlrRenewed?: number;
    expNlrLostCancelled?: number;
    expNlrRemaining?: number;

    // Renewal plan split into renewed and new for next year
    gnwpEurRenewed?: number;
    gnwpEurRenewedUwy?: number;
    gnwpEurRemaining?: number;
    gnwpEurFinalRemaining?: number;
    gnwpEurNewTotal?: number;

    nlrRenewed?: number;
    nlrRenewedUwy?: number;
    nlrRemaining?: number;
    nlrFinalRemaining?: number;
    nlrNewTotal?: number;

    rateChangeRenewed?: number;
    rateChangeRenewedUwy?: number;
    rateChangeRemaining?: number;
    rateChangeFinalRemaining?: number;
    rateChangeNewTotal?: number;

    retentionRateRenewed?: number;
    retentionRateRenewedUwy?: number;
    retentionRateRemaining?: number;
    retentionRateFinalRemaining?: number;
    retentionRateNewTotal?: number;

    // Inputs for outstanding accounts in the renewal
    targetRateChangeRemaining?: number;
    targetRetentionRateRemaining?: number;

    // Fiscal year metrics
    fyGnep?: number;
    fyGnwpEarnedInUwy?: number;
    fyNlr?: number;
    fyEarnedRateChange?: number;
    fyPlr?: number;
    fyPlrDelta?: number;

    llCount?: number;
    llPct?: number;
    llGnwpEur?: number;
    llExpectedLlPerYear?: number;
    llIntensity?: number;

    llGnwpEurRenewed?: number;
    llExpectedLlPerYearRenewed?: number;
    llIntensityRenewed?: number;
    llFrequencyReduction?: number;
    llPrunedInRenewal?: number;
}

export interface PfValidationCheckItem {
    id: string;
    severity: CheckSeverity;
    message: string;
    name?: string;
    description?: string;
    count: number;
}

export const UwStrategyBucketKeyAccount = 'Key Account';
export const UwStrategyBucketTotalExNewBusiness = 'Total ex. New Business';
export const UwStrategyBucketTotalExNewBusinessAndNotRenewable = 'Total ex. New Business & Not Renewable';
export const UwStrategyBucketNotRenewable = 'Not Renewable';
export const UwStrategyBucketNewBusiness = 'New Business';
export const UwStrategyBucketTotal = 'Total';

export const UwStrategyNonPfTiers = [
    UwStrategyBucketKeyAccount, UwStrategyBucketNotRenewable, UwStrategyBucketTotalExNewBusiness,
    UwStrategyBucketTotalExNewBusinessAndNotRenewable, UwStrategyBucketNewBusiness, UwStrategyBucketTotal
];

export const UwStrategyNonPfTiersShort = [UwStrategyBucketKeyAccount, UwStrategyBucketTotalExNewBusiness, UwStrategyBucketTotalExNewBusinessAndNotRenewable];

interface HistogramBucket {
    min: number;
    max: number;
    count: number;
}

export class PfReportDataHelper {
    static findSummaryRow(data: PfTargetUwySummaryRow[], bucket: string): PfTargetUwySummaryRow | undefined {
        const normalizedBucket = this.bucketNameNormalized(bucket);
        return data.find(it => it.bucket === normalizedBucket);
    }

    static findSummaryRowIndex(data: PfTargetUwySummaryRow[], bucket: string): number {
        const normalizedBucket = this.bucketNameNormalized(bucket);
        return data.findIndex(it => it.bucket === normalizedBucket);
    }

    static bucketNameNormalized(bucket: string): string {
        switch(bucket) {
            case UwStrategyBucketTotalExNewBusinessAndNotRenewable: return UwStrategyBucketTotal;
            default: return bucket;
        }
    }

    static autoHistogramWithVariableBucketSize(series: number[], numberOfBuckets: number): HistogramBucket[] {
        const min = Math.min(...series);
        const max = Math.max(...series);
        const bucketSize = (max - min) / numberOfBuckets;
        const buckets: HistogramBucket[] = [];
        let currentMin = min;
        for (let i = 0; i < numberOfBuckets; i++) {
            const currentMax = currentMin + bucketSize;
            const currentCount = series.filter(x => x >= currentMin && x < currentMax).length;
            buckets.push({ min: currentMin, max: currentMax, count: currentCount });
            currentMin = currentMax;
        }
        return buckets;
    }
}
